import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  LinearProgress,
  Alert,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

const CertificateReport = () => {
  const [usernames, setUsernames] = useState([]);
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const authHeaders = () => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRows([]);
    setProgress(0);

    try {
      // Step 1: get all usernames
      const res = await fetch(`${API_BASE}/certificate-report/stage7-usernames`, {
        headers: authHeaders(),
      });
      const json = await res.json();

      if (!json.success) throw new Error(json.message || 'Failed to fetch usernames');

      const names = json.data;
      setUsernames(names);

      if (names.length === 0) {
        setLoading(false);
        return;
      }

      // Step 2: check each username one by one
      const results = [];
      for (let i = 0; i < names.length; i++) {
        const username = names[i];
        try {
          const certRes = await fetch(`${API_BASE}/certificate-report/user-certificate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ username }),
          });
          const certJson = await certRes.json();
          if (certJson.success && certJson.data) {
            results.push(certJson.data);
          } else {
            results.push({ loginName: username, certificateNo: null, stage: null, externalSuccess: false });
          }
        } catch {
          results.push({ loginName: username, certificateNo: null, stage: null, externalSuccess: false });
        }

        setRows([...results]);
        setProgress(Math.round(((i + 1) / names.length) * 100));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = rows.filter(r =>
    r.loginName?.toLowerCase().includes(search.toLowerCase()) ||
    r.certificateNo?.toLowerCase().includes(search.toLowerCase())
  );

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleExport = () => {
    const data = filtered.map(r => ({
      'Login Name': r.loginName,
      'Certificate No': r.certificateNo || '-',
      'Stage': r.stage || '-',
      'External API Status': r.externalSuccess ? 'OK' : 'No data',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CertificateReport');
    XLSX.writeFile(wb, 'certificate_report_stage7.xlsx');
  };

  const stageColor = (stage) => {
    if (stage === '7') return 'success';
    if (stage === '7a') return 'info';
    if (stage === '7b') return 'warning';
    if (stage === '7c') return 'secondary';
    return 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700} color="#00024c">
              Certificate Report — Stage 7 Labs
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              All accredited labs (Stage 7/7a/7b/7c) with certificate numbers
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
              size="small"
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<GetAppIcon />}
              onClick={handleExport}
              disabled={rows.length === 0}
              size="small"
              sx={{ bgcolor: '#00024c' }}
            >
              Export Excel
            </Button>
          </Box>
        </Box>

        {/* Progress */}
        {loading && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                {usernames.length > 0
                  ? `Processing ${rows.length} / ${usernames.length} users…`
                  : 'Fetching usernames…'}
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 2, height: 6 }} />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Stats */}
        {rows.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Chip label={`Total: ${rows.length}`} color="primary" variant="outlined" />
            <Chip label={`With Certificate: ${rows.filter(r => r.certificateNo).length}`} color="success" variant="outlined" />
            <Chip label={`API Success: ${rows.filter(r => r.externalSuccess).length}`} color="info" variant="outlined" />
          </Box>
        )}

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search by login name or certificate no…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 340, mb: 2 }}
        />

        {/* Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#00024c' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Login Name</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Certificate No</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Stage</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>API Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {search ? 'No matching records' : 'No data available'}
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((row, idx) => (
                  <TableRow
                    key={row.loginName}
                    hover
                    sx={{ '&:nth-of-type(even)': { bgcolor: '#f5f5f5' } }}
                  >
                    <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{row.loginName}</TableCell>
                    <TableCell>
                      {row.certificateNo ? (
                        <Typography variant="body2" fontFamily="monospace" fontWeight={600} color="#00024c">
                          {row.certificateNo}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.stage ? (
                        <Chip label={`Stage ${row.stage}`} color={stageColor(row.stage)} size="small" />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.externalSuccess ? 'External API responded OK' : 'No data from external API'}>
                        <Chip
                          label={row.externalSuccess ? 'OK' : 'No data'}
                          color={row.externalSuccess ? 'success' : 'default'}
                          size="small"
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default CertificateReport;